import React, { useEffect, useState, useRef } from 'react';
import { Form, Col, Row, Tag } from 'antd';
import { FormInstance } from 'antd/lib/form';
import HeroTitle from 'components/theme_components/HeroTitle';
import ActivityCard from './ActivityCard';
import Title from 'components/Title';
import { PlusOutlined, RetweetOutlined } from '@ant-design/icons';
import TaskSButtonSFS from 'pages/workflow/workflowSFS/TasksButtonSFS';
import ButtonOperation from 'components/ButtonOperation';
import { useQuery } from 'react-query';
import {
  getThirdPartyAuthorizationListByTpid,
  getTPCurrentStatusByTpid,
} from 'services/thirdParty';
import IPen from 'icons/IPen';

export type ActivityListProps = {
  form: FormInstance;
  color: string;
  itemsCompanyHierarchyTree: any[];
  tptype: string;
  tpid: number;
  tpbrand?: string;
};

const ActivityList = ({
  form,
  color,
  itemsCompanyHierarchyTree,
  tptype,
  tpid,
  tpbrand = '',
}: ActivityListProps) => {
  // DETECTION D'EDITION INTELLIGENTE
  const [isUserEditing, setIsUserEditing] = useState(false);
  const lastActivityTime = useRef(Date.now());
  const inactivityTimer = useRef<NodeJS.Timeout>();

  // Détecter l'activité utilisateur (typing, clicking, etc.)
  useEffect(() => {
    const handleUserActivity = () => {
      lastActivityTime.current = Date.now();
      setIsUserEditing(true);

      // Clear le timer précédent
      if (inactivityTimer.current) {
        clearTimeout(inactivityTimer.current);
      }

      inactivityTimer.current = setTimeout(() => {
        setIsUserEditing(false);
      }, 3000);
    };

    const events = ['keydown', 'click', 'input', 'focus', 'change'];
    
    events.forEach(event => {
      document.addEventListener(event, handleUserActivity, true);
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleUserActivity, true);
      });
      if (inactivityTimer.current) {
        clearTimeout(inactivityTimer.current);
      }
    };
  }, []);

  // QUERY PRINCIPALE - Auto-refresh intelligent
  const { data, refetch, isFetching } = useQuery(
    ['getThirdPartyAuthorizationListByTpid', { tpid: Number(tpid) }],
    () =>
      getThirdPartyAuthorizationListByTpid({ tpid: Number(tpid) }).then(
        (res) => res.list || []
      ),
    { 
      enabled: !!tpid, 
      refetchOnWindowFocus: false,
      refetchInterval: isUserEditing ? false : 15000, 
      refetchIntervalInBackground: false,
      staleTime: 10000,
      keepPreviousData: true
    }
  );

  // QUERY POUR LE STATUS - Avec même logique d'auto-refresh
  const { data: currentStatus, isFetching: isfetch } = useQuery(
    ['getTPCurrentStatusByTpid', { tpid: Number(tpid) }],
    () =>
      getTPCurrentStatusByTpid({ tpid: Number(tpid), type: '' }).then(
        (res) => res.list || []
      ),
    {
      enabled: !!tpid,
      refetchOnWindowFocus: false,
      refetchInterval: isUserEditing ? false : 15000,
      refetchIntervalInBackground: false,
      staleTime: 10000,
      keepPreviousData: true
    }
  );

  // REFRESH AU CHANGEMENT D'ONGLET
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && tpid && !isUserEditing) {
        setTimeout(() => {
          refetch();
          // Optionnel: refetch aussi le status si nécessaire
        }, 500);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refetch, tpid, isUserEditing]);

  // Calculer le dernier status
  const lastStatus = [...(currentStatus || [])]
    .filter(rate => rate.statustype === "SFSGRADING")
    .sort(
      (a, b) =>
        new Date(b.statusdate).getTime() - new Date(a.statusdate).getTime()
    )[0];

  return (
    <Form form={form}>
      <HeroTitle
        form={form}
        color={color}
        display
        title={`limit${tptype}`}
        itemsCompanyHierarchyTree={itemsCompanyHierarchyTree}
      />

      <Row justify="center" className="pl-9 pr-9 mt-9">
        <Col
          span={tptype === 'SPM' || tptype === 'PM' ? 24 : 22}
          className="pl-0 pr-0"
        >
          {data &&
            [...data]
              .sort((a, b) => {
                const order = ['VN', 'VD', 'VO', 'SPR'];
                const aIndex = order.indexOf(a.prcode ?? '');
                const bIndex = order.indexOf(b.prcode ?? '');
                return (
                  (aIndex === -1 ? order.length : aIndex) -
                  (bIndex === -1 ? order.length : bIndex)
                );
              })
              .map((auth, index) => (
                <ActivityCard
                  tptype={tptype}
                  refetch={refetch}
                  data={auth}
                  key={index}
                />
              ))}
          {tptype === 'CONC' && (
            <Row gutter={[16, 16]} justify="center" style={{ marginTop: 24 }}>
              <Col>
                <TaskSButtonSFS
                  clientName=""
                  entityId={tpid}
                  entityReference=""
                  entityName="THIRDPARTY"
                  tptype={tptype}
                  dataInput={data}
                  currcode=""
                  funcode="evCreateTpAuthorization"
                  wizardsteps
                  refetch={() => refetch()}
                  width={700}
                  title="evcreatetpauthorization"
                  brand={tpbrand}
                >
                  <ButtonOperation
                    comcode="addlimit"
                    className="ut-btn-bordred"
                    icon={
                      <PlusOutlined className="color-orange font-size-20 cursorpointer mr-5 " />
                    }
                  />
                </TaskSButtonSFS>
              </Col>
            </Row>
          )}
        </Col>

        {/* Remove the grading section if tptype is "SPM" or "PM" */}
        {!(tptype === 'SPM' || tptype === 'PM') && (
          <Col
            span={2}
            className="d-flex flex-column align-center grading"
            style={{ marginBottom: 15 }}
          >
            <Title
              level={4}
              comcode="Garding"
              className="color-blue text-center font-size-18 font-weight-500"
            />
            <div className="d-flex align-center justify-between tag-icone mb-5 mt-0 ml-0 mr-0">
              <IPen className="font-size-18" style={{ cursor: 'pointer' }} />
              <RetweetOutlined className="text-center color-gray font-size-25 floatRight" />
            </div>
            {lastStatus && (
              <div className="align-center mb-4">
                <Tag
                  color="#e2632d"
                  className="tag-status tag-status-default align-center mt-20"
                >
                  <p className="text-white mb-0 font-size-30 bold">
                    {lastStatus.statuslabel}
                  </p>
                </Tag>

                <p className="text-center font-size-15 mt-3">
                  {lastStatus.statustypelabel}
                </p>
              </div>
            )}
          </Col>
        )}
      </Row>
    </Form>
  );
};

export default ActivityList;